import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Dialog, Divider, IconButton, Typography } from '@mui/material';
import { CancelIcon } from '@sharedCR/coreDesignSystem/icons';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { getConditionalObject } from 'utils/data';

import { closeDialogAction, openConfirmationDialogAction } from 'store/global/actions';

import { ButtonGroupWrapper } from '../../Buttons';
import { ModalsTestIds } from '../Modals.testIds';

import { getPaperProps, MOBILE_PAPER_SX, ModalSize, slotProps } from './ModalCDS.constants';

// TODO: add Label, Flag, Icon in Header and Back button in Dialog controls when it will be needed
const ModalCDS = forwardRef((props, ref) => {
  const dispatch = useDispatch();

  const { isMobile } = useBreakpoints();

  const formRef = useRef();

  const {
    id,
    isDisableClosing = false,
    withClosingConfirmation = false,
    header,
    customHead,
    dataTestId,
    closeButtonDataTestId,
    acceptButtonDataTestId,
    body,
    hasScrollY = true,
    afterCloseCallback,
    keepMounted,
    open,
    withButtons = false,
    withDivider = false,
    customButtons,
    onCancelButtonName = 'Cancel',
    onAcceptButtonName = 'Confirm',
    onAcceptClick,
    isAcceptButtonDisabled = false,
    size = ModalSize.SMALL,
    fullScreen = false,
    customDialogProps,
    isRestyled = false,
    fitContentWidth = false,
  } = props;

  const closeDialog = useCallback(
    id => {
      dispatch(closeDialogAction(id));
      afterCloseCallback?.();
    },
    [dispatch, afterCloseCallback],
  );

  useEffect(() => {
    window.addEventListener('popstate', closeDialog);

    return () => window.removeEventListener('popstate', closeDialog);
  }, [closeDialog]);

  const onClose = useCallback(() => {
    if (isDisableClosing) {
      return;
    }

    const isFormChanged = formRef.current?.dataset.ischanged === 'true';

    if (isFormChanged || withClosingConfirmation) {
      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm closing the dialog',
        description: 'Are you sure you want to close this dialog? All unsaved data will be lost.',
        onAccept: () => closeDialog(id),
      };

      return dispatch(openConfirmationDialogAction(params));
    }

    closeDialog(id);
  }, [dispatch, closeDialog, withClosingConfirmation, isDisableClosing, id]);

  const isFullScreen = fullScreen || isMobile;

  const paperProps = isMobile
    ? { sx: MOBILE_PAPER_SX }
    : getPaperProps({
        ...getConditionalObject(!isFullScreen, { size, fitContentWidth }),
      });

  // Derive the title element id from the modal's unique `id` so each dialog labels ITSELF, instead of
  // every ModalCDS sharing the static `confirmation-dialog-title`. With the static id, an always-mounted
  // sibling modal (e.g. the passkey-enrollment host) owned the first matching node, so a string-header
  // dialog's accessible name bled from that sibling ("Set up a passkey" on the drip Review modal —
  // DEV-4343). customHead modals keep the legacy static id (no element of ours to point at).
  const titleId = header ? `${id}-title` : 'confirmation-dialog-title';

  return (
    <Dialog
      PaperProps={paperProps}
      aria-describedby="confirmation-dialog-description"
      aria-labelledby={titleId}
      data-testid={dataTestId || ModalsTestIds.MODAL_CONFIRMATION_DIALOG}
      fullScreen={isFullScreen}
      keepMounted={keepMounted}
      open={open}
      ref={ref}
      scroll="body"
      slotProps={slotProps}
      onClose={onClose}
      {...customDialogProps}
    >
      <Box
        className={clsx('dialog-wrapper flex flex-col items-center px-20 py-24 w-full', {
          'gap-24': !withDivider,
          'gap-12': withDivider,
        })}
      >
        {(header || customHead) && (
          <Box className="dialog-header flex items-center justify-between gap-24 w-full px-4">
            {header ? (
              <Typography component="h5" id={titleId} variant="bold20">
                {header}
              </Typography>
            ) : (
              customHead
            )}

            {!isDisableClosing && (
              <IconButton
                disableRipple
                color="text.dark"
                data-testid={closeButtonDataTestId}
                onClick={onClose}
              >
                <CancelIcon size={IconSize[20]} />
              </IconButton>
            )}
          </Box>
        )}

        {withDivider && (
          <Box className="w-full px-4">
            <Divider />
          </Box>
        )}

        <Box
          className={clsx(
            'dialog-content flex flex-col items-center w-full overflow-x-hidden p-4',
            {
              'overflow-y-hidden': !hasScrollY,
              'overflow-y-auto': hasScrollY,
              '[&>*:first-child:not(form)]:!w-full [&>*:first-child:not(form)]:!p-0 [&_form]:!w-full [&_form]:!p-8':
                !isRestyled, // TODO: remove this rule when all old modals content being restyled
            },
          )}
        >
          {_.isString(body) ? (
            <Typography className="w-full" color="text.secondary" component="p" variant="regular14">
              {body}
            </Typography>
          ) : (
            body({ formRef, dialogId: id })
          )}
        </Box>

        {withButtons ? (
          <ButtonGroupWrapper
            alignment={HORIZONTAL_ALIGNMENT.RIGHT}
            className={clsx('dialog-actions w-full px-4', {
              'mt-12': withDivider,
            })}
          >
            {!isDisableClosing && (
              <Button className="dialog-action" size="large" variant="line" onClick={onClose}>
                {onCancelButtonName}
              </Button>
            )}

            <Button
              className="dialog-action"
              data-testid={acceptButtonDataTestId}
              disabled={isAcceptButtonDisabled}
              size="large"
              onClick={onAcceptClick}
            >
              {onAcceptButtonName}
            </Button>
          </ButtonGroupWrapper>
        ) : (
          customButtons
        )}
      </Box>
    </Dialog>
  );
});

ModalCDS.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  keepMounted: PropTypes.bool.isRequired,
  body: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  afterCloseCallback: PropTypes.func,
  isDisableClosing: PropTypes.bool,
  fitContentWidth: PropTypes.bool,
  hasScrollY: PropTypes.bool,
  header: PropTypes.string,
  customHead: PropTypes.node,
  dataTestId: PropTypes.string,
  closeButtonDataTestId: PropTypes.string,
  acceptButtonDataTestId: PropTypes.string,
  withClosingConfirmation: PropTypes.bool,
  isAcceptButtonDisabled: PropTypes.bool,
  withButtons: PropTypes.bool,
  withDivider: PropTypes.bool,
  customButtons: PropTypes.node,
  size: PropTypes.string,
  fullScreen: PropTypes.bool,
  customDialogProps: PropTypes.object,
  isRestyled: PropTypes.bool,
  onCancelButtonName: PropTypes.string,
  onAcceptButtonName: PropTypes.string,
  onAcceptClick: PropTypes.func,
};

export default ModalCDS;
